/** Speech recognition behind a provider interface so Azure can replace the browser later. */

export type SpeechOutcome =
  { status: 'pass' | 'retry'; transcript: string } | { status: 'unavailable' };

export interface SpeechProvider {
  readonly available: boolean;
  listen(
    target: { text: string; pinyin: string },
    charPinyin: Record<string, string[]>,
    timeoutMs?: number,
    /** Abort when the recording has ended: the recogniser is told to stop and gets a short
     * grace period to deliver what it heard, so the verdict never waits on its own timeout. */
    signal?: AbortSignal,
  ): Promise<SpeechOutcome>;
}

/** How long a stopped recogniser may take to deliver its final result before we move on. */
const RECOGNISER_STOP_GRACE_MS = 400;

/** The same idea for the recorder: how long a stopped MediaRecorder gets to deliver `stop` or
 * `error` before the take is settled with whatever it captured. */
const STOP_GRACE_MS = 2000;

interface RecognitionResultLike {
  results: ArrayLike<ArrayLike<{ transcript: string }>>;
}
interface RecognitionLike {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  continuous: boolean;
  onresult: ((e: RecognitionResultLike) => void) | null;
  onerror: ((e: unknown) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type RecognitionCtor = new () => RecognitionLike;

function ctor(): RecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: RecognitionCtor;
    webkitSpeechRecognition?: RecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export const stripTones = (p: string) =>
  p.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ü/g, 'v').toLowerCase();

/** Pass if the transcript contains the target text, or its toneless syllables in order. */
export function matches(
  transcript: string,
  target: { text: string; pinyin: string },
  charPinyin: Record<string, string[]>,
): boolean {
  const t = transcript.replace(/[\s，。、！？,.!?]/g, '');
  if (t.includes(target.text)) return true;
  const want = target.pinyin.split(/\s+/).map(stripTones);
  const got = [...t].map((c) => charPinyin[c]?.map(stripTones) ?? []);
  outer: for (let i = 0; i + want.length <= got.length; i++) {
    for (let k = 0; k < want.length; k++) if (!got[i + k]!.includes(want[k]!)) continue outer;
    return true;
  }
  return false;
}

export const browserSpeech: SpeechProvider = {
  get available() {
    return ctor() !== null;
  },
  listen(target, charPinyin, timeoutMs = 6000, signal) {
    const C = ctor();
    if (!C) return Promise.resolve({ status: 'unavailable' });
    if (signal?.aborted) return Promise.resolve({ status: 'retry', transcript: '' });
    return new Promise((resolve) => {
      const rec = new C();
      rec.lang = 'zh-CN';
      rec.interimResults = false;
      rec.maxAlternatives = 3;
      rec.continuous = false;
      let settled = false;
      const finish = (o: SpeechOutcome) => {
        if (settled) return;
        settled = true;
        try {
          rec.abort();
        } catch {
          /* ignore */
        }
        resolve(o);
      };
      rec.onresult = (e) => {
        const alts = Array.from(e.results[0] ?? []).map((a) => a.transcript);
        const hit = alts.find((a) => matches(a, target, charPinyin));
        finish({ status: hit ? 'pass' : 'retry', transcript: hit ?? alts[0] ?? '' });
      };
      rec.onerror = () => finish({ status: 'unavailable' });
      rec.onend = () => finish({ status: 'retry', transcript: '' });
      try {
        rec.start();
      } catch {
        finish({ status: 'unavailable' });
      }
      setTimeout(() => finish({ status: 'retry', transcript: '' }), timeoutMs);
      signal?.addEventListener(
        'abort',
        () => {
          try {
            rec.stop(); // deliver any pending result, then onend
          } catch {
            /* ignore */
          }
          setTimeout(() => finish({ status: 'retry', transcript: '' }), RECOGNISER_STOP_GRACE_MS);
        },
        { once: true },
      );
    });
  },
};

export function getSpeechProvider(): SpeechProvider {
  return browserSpeech;
}

// ---------- server-side pronunciation assessment (Azure via the Worker) ----------

export type AssessOutcome = {
  status: 'pass' | 'retry';
  /** The Worker's finer verdict; 'wrong' and 'no_speech' both surface as a retry to the child. */
  outcome: 'pass' | 'retry' | 'wrong' | 'no_speech';
  provider: string;
  transcript: string;
  score: number | null;
  /** Per-character detail when the vendor reports it (tones only from Mandarin-aware vendors). */
  syllables: Array<{
    text: string;
    expectedTone?: number;
    detectedTone?: number;
    toneScore?: number;
    pronunciationScore?: number;
  }>;
  /** The `speech_attempts` row id the Worker saves this take under, present when a profile was
   * attributed (profile/word/session context was sent). The save itself happens best-effort in
   * the background after the response is sent, so this id is not a guarantee the row exists yet
   * — or ever will, if that write fails. Lets the parent dashboard/result screen label this
   * specific attempt fair/harsh/lenient later. */
  attemptId?: string;
};

/**
 * Which profile (and, once one exists, which session) is doing the speaking, so the Worker can
 * persist the attempt against them. Set by the screen that starts a run (Play, StoryPlayer) and
 * cleared on unmount; `assessClip` sends whatever is known as extra query params.
 */
export interface SpeechContext {
  profileId: string;
  sessionId?: string;
}
let speechContext: SpeechContext | null = null;
export function setSpeechContext(ctx: SpeechContext | null) {
  speechContext = ctx;
  // A run with Speak is starting: wake the own scorer's container now (it can take a moment to
  // come up from its idle sleep, see workers/api/src/speech/scorer.ts) so it is warm well before
  // the child's first recording reaches it.
  if (ctx) warmSpeech();
}

/**
 * The context as it is right now, for a caller to hold across awaits (a recording, a decode) and
 * hand back to `assessClip`: the module-level one is replaced when the child switches sibling.
 */
export function currentSpeechContext(): SpeechContext | null {
  return speechContext ? { ...speechContext } : null;
}

/**
 * A miss is a tone the child got wrong with enough confidence to say so: a detected tone that
 * disagrees with the expected one (unless the pronunciation score was still decent), or — when
 * the vendor can't commit to a detected tone at all — a low tone score on its own. Neutral tone
 * (0) and characters the vendor didn't score are never misses: there is nothing to correct.
 */
export function toneFeedback(
  syllables: AssessOutcome['syllables'],
  pinyin: string | undefined,
): {
  misses: Array<{
    index: number;
    text: string;
    syllable: string;
    expectedTone: number;
    detectedTone?: number;
  }>;
} {
  const parts = pinyin ? pinyin.trim().split(/\s+/) : [];
  const aligned = parts.length === syllables.length;
  const misses: Array<{
    index: number;
    text: string;
    syllable: string;
    expectedTone: number;
    detectedTone?: number;
  }> = [];
  syllables.forEach((syl, index) => {
    const expectedTone = syl.expectedTone;
    if (expectedTone === undefined || expectedTone < 1 || expectedTone > 4) return;
    const { detectedTone, toneScore } = syl;
    const miss =
      (detectedTone !== undefined &&
        detectedTone !== expectedTone &&
        (toneScore === undefined || toneScore < 70)) ||
      (detectedTone === undefined && toneScore !== undefined && toneScore < 50);
    if (!miss) return;
    misses.push({
      index,
      text: syl.text,
      syllable: aligned ? (parts[index] ?? '') : '',
      expectedTone,
      ...(detectedTone !== undefined ? { detectedTone } : {}),
    });
  });
  return { misses };
}

/**
 * Turns a parent's "Unfair" tap on a Speak result into a specific label, from the attempt's own
 * verdict — never asks the parent to pick. The engine said `pass`: the parent thinks that was too
 * generous ('lenient'). Anything else (`retry`/`wrong`/`no_speech`): the parent thinks the child
 * was marked down unfairly ('harsh').
 */
export function unfairLabel(
  outcome: 'pass' | 'retry' | 'wrong' | 'no_speech',
): 'harsh' | 'lenient' {
  return outcome === 'pass' ? 'lenient' : 'harsh';
}

/** Same-origin in production (the Worker serves the SPA); overridable for local dev. */
const API_ORIGIN = ((import.meta.env.VITE_API_ORIGIN as string | undefined) ?? '').replace(
  /\/+$/,
  '',
);

/**
 * How the Worker recognises the signed-in parent: the Better Auth session cookie (api mode).
 * Unset in prototype mode, where assessment is skipped.
 */
export type SpeechAuth = { mode: 'cookie' };
let speechAuth: SpeechAuth | null = null;
export function setSpeechAuth(auth: SpeechAuth | null) {
  speechAuth = auth;
}

/**
 * Fire-and-forget: ping the Worker's speech-warm route so the own scorer's Cloudflare Container
 * is awake ahead of the first recording (see `setSpeechContext`, which calls this). Never throws
 * and does not wait for a response; a slow or failed warm-up just means the first `assessClip`
 * pays the cold-start cost instead, which its own timeout already degrades gracefully from.
 */
export function warmSpeech(): void {
  if (!speechAuth) return;
  fetch(`${API_ORIGIN}/v1/speech/warm`, { method: 'POST', credentials: 'include' }).catch(() => {
    /* best-effort only */
  });
}

/** How long `assessClip` waits for the Worker before giving up and letting the caller fall back
 * to the browser recogniser. A cold own-scorer container (see warmSpeech) can take a few seconds
 * to start; 8s is enough for that but still short enough that a child recording something isn't
 * left waiting on a stalled request past the point a retry would already be useful. */
const ASSESS_TIMEOUT_MS = 8000;

/**
 * Score a recorded clip against the reference text with the Worker's pronunciation assessment
 * (whichever vendor it is configured with). Resolves null whenever assessment isn't possible
 * (no session, provider not configured, the clip can't be decoded, network, timeout) so the
 * caller can fall back to the browser recogniser.
 *
 * Who the take is attributed to is fixed on entry, before decoding yields: `meta.context` if the
 * caller captured one when recording started (explicit `null` means none), else the context as it
 * is now. Reading it after the awaits would credit a sibling switched to meanwhile.
 */
export async function assessClip(
  blob: Blob,
  target: { text: string; pinyin?: string },
  meta?: { wordId?: string; attempt?: number; context?: SpeechContext | null },
): Promise<AssessOutcome | null> {
  if (!speechAuth) return null;
  const context = meta?.context !== undefined ? meta.context : currentSpeechContext();
  try {
    const headers: Record<string, string> = { 'Content-Type': 'audio/wav' };
    const wav = await toWav16k(blob);
    if (!wav) return null;
    const q = new URLSearchParams({ text: target.text });
    if (target.pinyin) q.set('pinyin', target.pinyin);
    if (context?.profileId) q.set('profile', context.profileId);
    if (context?.sessionId) q.set('session', context.sessionId);
    if (meta?.wordId) q.set('word', meta.wordId);
    if (meta?.attempt !== undefined) q.set('attempt', String(meta.attempt));
    const r = await fetch(`${API_ORIGIN}/v1/speech/assess?${q}`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: wav,
      signal: AbortSignal.timeout(ASSESS_TIMEOUT_MS),
    });
    if (!r.ok) return null;
    const data = (await r.json()) as {
      provider?: string;
      outcome?: AssessOutcome['outcome'];
      transcript?: string;
      score?: number;
      syllables?: AssessOutcome['syllables'];
      attemptId?: string;
    };
    if (!data.outcome || !data.provider) return null;
    return {
      status: data.outcome === 'pass' ? 'pass' : 'retry',
      outcome: data.outcome,
      provider: data.provider,
      transcript: data.transcript ?? '',
      score: data.score ?? null,
      syllables: data.syllables ?? [],
      ...(data.attemptId !== undefined ? { attemptId: data.attemptId } : {}),
    };
  } catch {
    return null;
  }
}

function audioContextCtor(): typeof AudioContext | undefined {
  if (typeof window === 'undefined') return undefined;
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  );
}

/** Decode whatever MediaRecorder produced into PCM. Null when the browser can't. */
async function decodeClip(blob: Blob): Promise<AudioBuffer | null> {
  try {
    const Ctor = audioContextCtor();
    if (!Ctor) return null;
    const ctx = new Ctor();
    try {
      return await ctx.decodeAudioData(await blob.arrayBuffer());
    } finally {
      void ctx.close().catch(() => {});
    }
  } catch {
    return null;
  }
}

/** Mix down to mono at `rate`, multiplied by `gain`, and pack as 16-bit PCM WAV. */
async function renderWav(
  decoded: AudioBuffer,
  rate: number,
  gain = 1,
): Promise<ArrayBuffer | null> {
  try {
    if (typeof OfflineAudioContext === 'undefined') return null;
    const length = Math.ceil(decoded.duration * rate);
    if (length === 0) return null;
    const off = new OfflineAudioContext(1, length, rate);
    const src = off.createBufferSource();
    src.buffer = decoded;
    const g = off.createGain();
    g.gain.value = gain;
    src.connect(g);
    g.connect(off.destination);
    src.start();
    const mono = (await off.startRendering()).getChannelData(0);
    const out = new ArrayBuffer(44 + mono.length * 2);
    const v = new DataView(out);
    const str = (at: number, s: string) => {
      for (let i = 0; i < s.length; i++) v.setUint8(at + i, s.charCodeAt(i));
    };
    str(0, 'RIFF');
    v.setUint32(4, 36 + mono.length * 2, true);
    str(8, 'WAVE');
    str(12, 'fmt ');
    v.setUint32(16, 16, true);
    v.setUint16(20, 1, true);
    v.setUint16(22, 1, true);
    v.setUint32(24, rate, true);
    v.setUint32(28, rate * 2, true);
    v.setUint16(32, 2, true);
    v.setUint16(34, 16, true);
    str(36, 'data');
    v.setUint32(40, mono.length * 2, true);
    for (let i = 0; i < mono.length; i++) {
      const x = Math.max(-1, Math.min(1, mono[i]!));
      v.setInt16(44 + i * 2, x < 0 ? x * 0x8000 : x * 0x7fff, true);
    }
    return out;
  } catch {
    return null;
  }
}

/** Decode whatever MediaRecorder produced and re-encode as 16 kHz mono PCM WAV. */
async function toWav16k(blob: Blob): Promise<ArrayBuffer | null> {
  const decoded = await decodeClip(blob);
  return decoded ? renderWav(decoded, 16000) : null;
}

/** Loudest absolute sample across every channel, 0 (digital silence) to 1 (clipping). */
export function peakLevel(channels: ReadonlyArray<ArrayLike<number>>): number {
  let peak = 0;
  for (const samples of channels)
    for (let i = 0; i < samples.length; i++) {
      const a = Math.abs(samples[i] as number);
      if (a > peak) peak = a;
    }
  return Math.min(1, peak);
}

/** Playback is lifted so the clip's peak lands near `PLAYBACK_TARGET_PEAK`. A child's voice a
 * metre from an iPad records quietly even with auto gain, and the "Listen again" replay was
 * barely audible; capped so a near-silent take doesn't become a blast of room noise. */
export const PLAYBACK_TARGET_PEAK = 0.9;
export const PLAYBACK_MAX_GAIN = 8;
export function playbackGain(peak: number): number {
  if (!(peak > 0)) return 1;
  return Math.max(1, Math.min(PLAYBACK_MAX_GAIN, PLAYBACK_TARGET_PEAK / peak));
}

export interface ClipAnalysis {
  /** True peak of the decoded audio, 0..1. */
  peak: number;
  /** The same take as a WAV with its level lifted for the child to hear themselves clearly. */
  playback: Blob;
}

/**
 * Decode a recording once and measure it exactly. This is the level the silence check trusts:
 * the live meter in `recordClip` only samples ~20ms in every 50 and can miss a short syllable
 * outright, and on iOS its AudioContext may sit suspended and read flat silence. Null when the
 * clip can't be decoded (the caller then falls back to the live meter's reading).
 */
export async function analyseClip(blob: Blob): Promise<ClipAnalysis | null> {
  const decoded = await decodeClip(blob);
  if (!decoded) return null;
  const channels: Float32Array[] = [];
  for (let c = 0; c < decoded.numberOfChannels; c++) channels.push(decoded.getChannelData(c));
  const peak = peakLevel(channels);
  const wav = await renderWav(decoded, decoded.sampleRate, playbackGain(peak));
  if (!wav) return null;
  return { peak, playback: new Blob([wav], { type: 'audio/wav' }) };
}

export interface RecordedClip {
  blob: Blob | null;
  /** Loudest level the live meter saw during recording, 0 (silence) to 1 (clipping). 0 when it
   * couldn't be measured (no AudioContext, or recording failed before anything was captured).
   * Coarse: prefer `analyseClip` on the blob when deciding whether the child said anything. */
  peak: number;
}

/**
 * Record a short clip from the mic and resolve with the audio blob (and its peak level) once
 * `ms` have elapsed, or sooner when `opts.endpoint` hears the child finish. `blob` is `null`
 * (this never rejects) when recording isn't possible: no `getUserMedia`, no `MediaRecorder`,
 * the mic is denied, or nothing was captured. Always stops the mic tracks.
 */
/** Ask for the browser's voice processing explicitly: auto gain in particular is what keeps a
 * soft-spoken six-year-old above the silence floor, and not every browser turns it on for a bare
 * `audio: true`. These are all "ideal" constraints, so an unsupported one is ignored, not fatal. */
export const MIC_CONSTRAINTS: MediaStreamConstraints = {
  audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
};

export interface RecordOptions {
  /** Stop early once the child has spoken and then gone quiet: after `silenceMs` of continuous
   * quiet following speech, never before `minMs`. Without it the full window is held. When the
   * live meter can't hear anything (no AudioContext, a suspended one on iOS) speech is never
   * detected and the full window is held, so a silent take still gets its "didn't hear you". */
  endpoint?: { minMs: number; silenceMs: number };
}

/** Live-meter levels (0..1) that count as speech starting and as quiet afterwards. The meter
 * reads the loudest sample deviation in its 50 ms tick; a normal voice with auto gain sits well
 * above 0.06. These are floors: the first ticks of a take, before anyone speaks, measure the
 * room, and both levels rise with it (speech to 3x the room, quiet to 1.6x) so a fan or a TV
 * in the background does not count as the child still talking. */
export const ENDPOINT_LEVELS = { speech: 0.06, quiet: 0.03, roomTicks: 4 } as const;

/** How the last take ended, for the on-device log and for tuning: `speechAt`/`stoppedAt` are ms
 * from the start of the take; `cap` means the full window ran. */
export interface EndpointReport {
  cap: boolean;
  speechAt: number | null;
  stoppedAt: number;
  room: number;
}
export let lastEndpoint: EndpointReport | null = null;

export async function recordClip(ms: number, opts: RecordOptions = {}): Promise<RecordedClip> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return { blob: null, peak: 0 };
  }
  if (typeof MediaRecorder === 'undefined') return { blob: null, peak: 0 };
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
  } catch {
    return { blob: null, peak: 0 };
  }
  let peak = 0;
  // Set once the recorder is running; the meter calls it on every tick.
  let onLevel: ((p: number) => void) | null = null;
  const stopLevelMeter = startLevelMeter(stream, (p) => {
    peak = Math.max(peak, p);
    onLevel?.(p);
  });
  try {
    const blob = await new Promise<Blob | null>((resolve) => {
      const chunks: BlobPart[] = [];
      let rec: MediaRecorder;
      try {
        rec = new MediaRecorder(stream);
      } catch {
        resolve(null);
        return;
      }
      let cap: ReturnType<typeof setTimeout> | undefined;
      let backstop: ReturnType<typeof setTimeout> | undefined;
      const settle = (blob: Blob | null) => {
        if (cap !== undefined) clearTimeout(cap);
        if (backstop !== undefined) clearTimeout(backstop);
        resolve(blob);
      };
      const collected = () =>
        chunks.length ? new Blob(chunks, { type: rec.mimeType || 'audio/webm' }) : null;
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      rec.onstop = () => settle(collected());
      rec.onerror = () => settle(null);
      let stopped = false;
      const stop = () => {
        if (stopped) return;
        stopped = true;
        if (cap !== undefined) clearTimeout(cap);
        // `rec.stop()` is a request, not an answer: this promise settles only from `onstop` or
        // `onerror`. A recorder that delivers neither — the same wedged-media-stack shape the
        // Safari review confirmed for playback — would leave it pending for ever, and with it
        // the take, the mic light and the child, who sits on "Listening…" with no way out. So
        // the request gets a deadline of its own, and then we take whatever was captured.
        if (backstop === undefined) backstop = setTimeout(() => settle(collected()), STOP_GRACE_MS);
        try {
          rec.stop();
        } catch {
          settle(null);
        }
      };
      try {
        rec.start();
      } catch {
        // Safari can refuse start() synchronously (NotSupportedError when it will not record the
        // default container). Thrown from inside this executor it rejects the promise, and this
        // function's contract is a value, never a rejection: SpeakPrompt does not recover one, so
        // the child would be left on "Listening…" with no way out of the take.
        settle(null);
        return;
      }
      const startedAt = Date.now();
      lastEndpoint = null;
      // Endpoint bookkeeping, only used when opts.endpoint is set.
      let speechAt: number | null = null;
      const room: number[] = [];
      const report = (capHit: boolean) => {
        if (!opts.endpoint) return;
        lastEndpoint = {
          cap: capHit,
          speechAt,
          stoppedAt: Date.now() - startedAt,
          room: room.length ? Math.max(...room) : 0,
        };
      };
      cap = setTimeout(() => {
        report(true);
        stop();
      }, ms);
      if (opts.endpoint) {
        const { minMs, silenceMs } = opts.endpoint;
        let spoke = false;
        let quietSince: number | null = null;
        let speechLevel: number = ENDPOINT_LEVELS.speech;
        let quietLevel: number = ENDPOINT_LEVELS.quiet;
        onLevel = (p) => {
          const now = Date.now();
          // The first ticks before anyone speaks measure the room; both levels rise with it.
          if (!spoke && room.length < ENDPOINT_LEVELS.roomTicks && p < ENDPOINT_LEVELS.speech) {
            room.push(p);
            const floor = Math.max(...room);
            speechLevel = Math.max(ENDPOINT_LEVELS.speech, floor * 3);
            quietLevel = Math.max(ENDPOINT_LEVELS.quiet, floor * 1.6);
          }
          if (p >= speechLevel) {
            spoke = true;
            speechAt ??= now - startedAt;
            quietSince = null;
            return;
          }
          if (!spoke || p >= quietLevel) return;
          quietSince ??= now;
          if (now - quietSince >= silenceMs && now - startedAt >= minMs) {
            report(false);
            stop();
          }
        };
      }
    });
    return { blob, peak };
  } catch {
    // The same contract, for anything else in there that throws: a take that could not be made is
    // a take with no audio, which every caller already handles.
    return { blob: null, peak };
  } finally {
    onLevel = null;
    stopLevelMeter();
    for (const track of stream.getTracks()) track.stop();
  }
}

/**
 * Sample the mic's level every ~50ms via an AnalyserNode, reporting the max absolute deviation
 * from silence (128) as a 0..1 fraction on each tick. Returns a cleanup function. Best-effort:
 * swallows every failure (headless test browsers, missing AudioContext, etc.) so it never affects
 * the recording it's measuring.
 */
function startLevelMeter(stream: MediaStream, onSample: (peak: number) => void): () => void {
  try {
    const Ctor = audioContextCtor();
    if (!Ctor) return () => {};
    const ctx = new Ctor();
    // iOS may hand back a suspended context this far from the tap; a suspended analyser reads
    // flat silence, which is exactly the false "we didn't hear you" this meter must not cause.
    void ctx.resume().catch(() => {});
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);
    const iv = setInterval(() => {
      analyser.getByteTimeDomainData(data);
      let max = 0;
      for (let i = 0; i < data.length; i++) {
        const dev = Math.abs((data[i] as number) - 128);
        if (dev > max) max = dev;
      }
      onSample(max / 128);
    }, 50);
    return () => {
      clearInterval(iv);
      try {
        source.disconnect();
        analyser.disconnect();
      } catch {
        /* ignore */
      }
      void ctx.close().catch(() => {});
    };
  } catch {
    return () => {};
  }
}

let micPrimed = false;

/**
 * Best-effort: request the mic once so the browser's permission prompt (which otherwise swallows
 * the child's first tap on the record button) has already been resolved by the time they get
 * there. Safe to call repeatedly; only ever runs once per page load, and never throws.
 */
export async function primeMicrophone(): Promise<void> {
  if (micPrimed) return;
  micPrimed = true;
  try {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return;
    const stream = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
    for (const track of stream.getTracks()) track.stop();
  } catch {
    /* ignore: at most we've lost the head start, permission flow still works from the mic tap */
  }
}
